import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Modal from "../ui/Modal";
import LazyImage from "../ui/LazyImage";
import { createBlurDataURL } from "../../utils/image.utils";
import whatsappIcon from "../../assets/images/icon-whatsapp.png";
import { buildProductOrderMessage, buildWhatsAppUrl } from "../../domain/shared/commerce.utils";
import type { OrganizationPageProduct as BusinessPageProduct } from "../../presentation/organizations/organizationPage.types";

interface ProductModalProps {
  product: BusinessPageProduct | null;
  organizationName: string;
  whatsapp?: string;
  fallbackImage: string;
  onClose: () => void;
  labels: {
    weight: string;
    flavor: string;
    presentation: string;
    action: string;
    multiPresentationHelp?: string;
  };
  mode?: "catalog" | "featured";
}

const presentationLabel = (presentation: { weight?: string; flavor?: string }) => presentation.weight || presentation.flavor || "";

export const openProductOrder = (product: BusinessPageProduct, organizationName: string, whatsapp: string | undefined, quantity = 1, presentationIndex = 0, mode: "catalog" | "featured" = "catalog") => {
  if (!whatsapp) return;
  const presentation = product.presentations?.[presentationIndex];
  const message = buildProductOrderMessage({ productName: product.name, organizationName, quantity, presentation: presentation ? presentationLabel(presentation) : undefined, mode: mode === "featured" ? "interest" : "order" });
  const url = buildWhatsAppUrl(whatsapp, message);
  if (url) window.open(url, "_blank");
};

const openProductInquiry = (product: BusinessPageProduct, organizationName: string, whatsapp: string | undefined, presentationIndex = 0) => {
  if (!whatsapp) return;
  const presentation = product.presentations?.[presentationIndex];
  const message = buildProductOrderMessage({
    productName: product.name,
    organizationName,
    presentation: presentation ? presentationLabel(presentation) : undefined,
    price: presentation?.price ?? product.price,
    mode: "order",
  });
  const url = buildWhatsAppUrl(whatsapp, message);
  if (url) window.open(url, "_blank");
};

const ProductModal: React.FC<ProductModalProps> = ({ product, organizationName, whatsapp, fallbackImage, onClose, labels, mode = "catalog" }) => {
  const [presentationIndex, setPresentationIndex] = useState(0);
  useEffect(() => { setPresentationIndex(0); }, [product?.id]);

  const order = () => {
    if (product) openProductInquiry(product, organizationName, whatsapp, presentationIndex);
  };

  if (mode === "featured") return (
    <Modal isOpen={Boolean(product)} onClose={onClose}>
      {product && (
        <div className="mx-auto flex w-full max-w-xs flex-col items-center rounded-2xl border border-primary/20 bg-white p-6 shadow-2xl">
          <div className="mb-4 h-40 w-40 overflow-hidden rounded-xl border-2 border-primary/20 bg-white shadow"><LazyImage src={Array.isArray(product.image) ? product.image[0] : product.image} alt={product.name} width="100%" height="100%" className="h-full w-full" placeholder={createBlurDataURL(10, 10)} fallbackSrc={fallbackImage} priority objectFit="cover" /></div>
          <h2 className="mb-2 text-center text-2xl font-bold text-primary">{product.name}</h2>
          <p className="mb-4 text-center text-base leading-relaxed text-gray-700">{product.description}</p>
          {product.presentations && product.presentations.length >= 2 && <div className="mb-4 w-full"><label className="mb-2 block text-sm font-semibold text-gray-700">{product.presentations[presentationIndex].weight ? labels.weight : labels.flavor}</label><select value={presentationIndex} onChange={(event) => setPresentationIndex(Number(event.target.value))} className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary">{product.presentations.map((presentation, index) => <option key={`${product.id}-presentation-${index}`} value={index}>{presentationLabel(presentation)} - {presentation.price}</option>)}</select></div>}
          {product.presentations?.length === 1 && <p className="mb-4 w-full text-center text-sm font-semibold text-gray-700">{labels.presentation}: {presentationLabel(product.presentations[0])}</p>}
          <div className="mb-4 rounded-lg bg-primary/10 px-6 py-2 text-center text-3xl font-extrabold text-primary shadow">{product.presentations?.[presentationIndex]?.price ?? product.price}</div>
          {product.presentations && product.presentations.length >= 2 && labels.multiPresentationHelp && <p className="mb-4 text-center text-sm text-gray-500">{labels.multiPresentationHelp}</p>}
          {whatsapp && <button type="button" className="inline-flex items-center justify-center gap-2 rounded-lg bg-whatsapp px-5 py-3 font-semibold text-white shadow transition hover:bg-whatsapp-dark" onClick={order}><img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />{labels.action}</button>}
        </div>
      )}
    </Modal>
  );

  return (
    <Modal isOpen={Boolean(product)} onClose={onClose}>
      {product && (
        <div className="mx-auto flex w-full max-w-2xl flex-col items-center rounded-2xl border border-primary/20 bg-white p-4 shadow-2xl sm:p-6">
          <div className="mx-auto mb-4 h-56 w-full overflow-hidden rounded-xl border-2 border-primary/20 shadow sm:h-96">
            <LazyImage src={Array.isArray(product.image) ? product.image[0] : product.image} alt={product.name} width="100%" height="100%" className="h-full w-full object-cover" placeholder={createBlurDataURL(10, 10)} fallbackSrc={fallbackImage} priority objectFit="cover" />
          </div>
          <h2 className="mb-2 text-center text-2xl font-bold text-primary">{product.name}</h2>
          <p className="mb-4 text-center text-base leading-relaxed text-gray-700">{product.description}</p>
          {product.presentations && product.presentations.length >= 2 && (
            <div className="mb-4 w-full">
              <label className="mb-2 block text-sm font-semibold text-gray-700">{product.presentations[presentationIndex].weight ? labels.weight : labels.flavor}</label>
              <select value={presentationIndex} onChange={(event) => setPresentationIndex(Number(event.target.value))} className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary">
                {product.presentations.map((presentation, index) => <option key={`${product.id}-presentation-${index}`} value={index}>{presentationLabel(presentation)} - {presentation.price}</option>)}
              </select>
            </div>
          )}
          {product.presentations?.length === 1 && <p className="mb-4 w-full text-center text-sm font-semibold text-gray-700">{labels.presentation}: {presentationLabel(product.presentations[0])}</p>}
          <div className="mb-4 rounded-lg bg-primary/10 px-5 py-2 text-center text-2xl font-extrabold text-primary shadow sm:text-3xl">{product.presentations?.[presentationIndex]?.price ?? product.price}</div>
          <div className="mt-2 flex gap-3"><motion.button type="button" className="inline-flex items-center justify-center gap-2 rounded-lg bg-whatsapp px-6 py-3 text-lg font-semibold text-white shadow transition hover:bg-whatsapp-dark" whileTap={{ scale: 0.98 }} onClick={order}><img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />{labels.action}</motion.button></div>
        </div>
      )}
    </Modal>
  );
};

export default ProductModal;
